<template>
  <div id="publishing-article-add">
    <div class="ipub-h1 mar-b-20">
      {{ params.id ? 'Edit' : 'Create a New' }} Article
    </div>
    <ipub-form
      ref="article-add-form"
      :model="params"
      autocomplete="off"
      :rules="rules"
    >
      <ipub-row :gutter="20">
        <ipub-col :span="2">
          <ipub-form-item prop="journalId" label="Journal">
            <ipub-select
              v-model="params.journalId"
              placeholder="Select Journal"
              disabled
              :options="journalList"
            />
          </ipub-form-item>
        </ipub-col>
        <ipub-col :span="2">
          <ipub-form-item prop="typeCode" label="Article Type">
            <ipub-select
              v-model="params.typeCode"
              @change="onTypeChange"
              search
              @search="getType"
              placeholder="Select Article Type"
            >
              <ipub-select-option
                v-for="item in typeList"
                :disabled="!item.released"
                :key="item.code"
                :value="item.code"
              >
                {{ item.name }}
                <template v-if="item.otherName"
                  >({{ item.otherName }})</template
                >
              </ipub-select-option>
            </ipub-select>
          </ipub-form-item>
        </ipub-col>
        <ipub-col :span="1">
          <ipub-form-item prop="oaed" label=" ">
            <ipub-radio
              class="mar-b-10 line-h-40"
              style="margin-top: 30px"
              label="OA"
              :disabled="oaDisable"
              @change="changeOA"
              :checked="params.oaed"
            />
          </ipub-form-item>
        </ipub-col>
        <ipub-col :span="1">
          <ipub-form-item prop="subscribed" label=" ">
            <ipub-radio
              class="mar-b-10 line-h-40"
              style="margin-top: 30px"
              label="Subscription"
              @change="changeSubscribed"
              :checked="params.subscribed"
            />
          </ipub-form-item>
        </ipub-col>
        <ipub-col v-if="isRe" :span="2.5">
          <ipub-form-item
            prop="retractionCode"
            label="Associate Manuscript No."
          >
            <ipub-input
              placeholder="Associate Manuscript No."
              @blur="getByCode"
              :disabled="!!params.id"
              v-model.trim="params.retractionCode"
            />
          </ipub-form-item>
        </ipub-col>
        <ipub-col :span="isRe ? 2.5 : 5">
          <ipub-form-item prop="doi" label="DOI">
            <ipub-input placeholder="DOI" v-model.trim="params.doi" />
          </ipub-form-item>
        </ipub-col>
        <ipub-col :span="1">
          <ipub-form-item prop="doiUsed" label=" ">
            <ipub-radio
              class="mar-b-10 line-h-40"
              style="margin-top: 30px"
              label="Show"
              @change="changeDoiUser"
              :checked="params.doiUsed"
            />
          </ipub-form-item>
        </ipub-col>
      </ipub-row>
      <ipub-row :gutter="20" v-if="params.id">
        <ipub-col :span="6">
          <ipub-form-item prop="code" label="Manuscript No.">
            <ipub-input
              placeholder="Manuscript No."
              disabled
              v-model.trim="params.code"
            />
          </ipub-form-item>
        </ipub-col>
        <ipub-col :span="6">
          <ipub-form-item prop="pii" label="Pii.">
            <ipub-input
              placeholder="Pii."
              :disabled="params.id ? false : true"
              v-model.trim="params.pii"
            />
          </ipub-form-item>
        </ipub-col>
      </ipub-row>
      <ipub-row :gutter="20">
        <ipub-col :span="3">
          <ipub-form-item prop="volumeId" label="Volume">
            <ipub-select
              placeholder="Select Volume"
              v-model="params.volumeId"
              optionTitle="code"
              @change="getIssueCode"
              :options="volumeList"
            />
          </ipub-form-item>
        </ipub-col>
        <ipub-col :span="3">
          <ipub-form-item prop="issueId" label="Issue">
            <ipub-select
              placeholder="Select Issue"
              optionTitle="code"
              @change="onIssueCodeChange"
              v-model="params.issueId"
              :options="issueList"
            />
          </ipub-form-item>
        </ipub-col>
        <ipub-col :span="4">
          <div class="flex" style="align-items: center">
            <ipub-form-item
              style="width: 50%"
              prop="startPage"
              label="Start Page Number"
            >
              <ipub-input
                placeholder="Start Page Number"
                number
                v-model="params.startPage"
              />
            </ipub-form-item>
            <div>—</div>
            <ipub-form-item
              style="width: 50%"
              prop="endPage"
              label="End Page Number"
            >
              <ipub-input
                placeholder="End Page Number"
                number
                :min="params.startPage || 0"
                v-model="params.endPage"
              />
            </ipub-form-item>
          </div>
        </ipub-col>
        <ipub-col :span="2">
          <ipub-form-item prop="sort" label="Sort">
            <ipub-input number placeholder="Sort" v-model="params.sort" />
          </ipub-form-item>
        </ipub-col>
      </ipub-row>
      <ipub-row :gutter="20">
        <ipub-col v-if="!isRe" :span="3">
          <ipub-form-item prop="submittedDate" label="Received Date">
            <ipub-date-picker
              placeholder="Received Date"
              v-model="params.submittedDate"
            />
          </ipub-form-item>
        </ipub-col>
        <ipub-col :span="isRe ? 4 : 3">
          <ipub-form-item prop="revisedDate" label="Revised Date">
            <ipub-date-picker
              placeholder="Revised Date"
              v-model="params.revisedDate"
            />
          </ipub-form-item>
        </ipub-col>
        <ipub-col :span="isRe ? 4 : 3">
          <ipub-form-item prop="acceptDate" label="Accept Date">
            <ipub-date-picker
              placeholder="Accept Date"
              v-model="params.acceptDate"
              autocomplete="off"
            />
          </ipub-form-item>
        </ipub-col>
        <ipub-col :span="isRe ? 4 : 3">
          <ipub-form-item prop="publishedDate" label="Published Date">
            <ipub-date-picker
              placeholder="Published Date"
              v-model="params.publishedDate"
              autocomplete="off"
              name="published"
            />
          </ipub-form-item>
        </ipub-col>
      </ipub-row>
      <ipub-row :gutter="20">
        <ipub-col :span="6">
          <ipub-form-item v-if="!isRe" prop="sectionIdList" label="Section">
            <ipub-select
              placeholder="Section"
              :options="sectionList"
              mode="multiple"
              :tree="true"
              optionKey="id"
              v-model="params.sectionIdList"
            />
          </ipub-form-item>
        </ipub-col>
        <ipub-col :span="!isRe ? 6 : 12">
          <ipub-form-item v-if="!isRe" prop="sectionIdList" label="Topic">
            <!-- <ipub-select placeholder="Topic"
            :disabled="disable"
                         :options="topicList"
                         optionKey="id"
                         optionTitle="titleText"
                         v-model="params.topicId" /> -->
            <ipub-select
              placeholder="Topics"
              :clear="true"
              search
              @search="getTopic"
              v-model="params.topicId"
            >
              <ipub-select-option
                v-for="item in topicList"
                :key="item.id"
                v-show="!item.disabled"
                :value="item.id"
                >{{ item.titleText }}
              </ipub-select-option>
            </ipub-select>
          </ipub-form-item>
        </ipub-col>
      </ipub-row>

      <ipub-form-item v-if="!isRe" prop="specialId" label="Special Issue">
        <ipub-select
          placeholder="Special Issue"
          :options="specialList"
          search
          :clear="true"
          @search="getSpecial"
          optionTitle="titleText"
          v-model="params.specialId"
        />
      </ipub-form-item>
      <ipub-row :gutter="20">
        <ipub-col :span="3">
          <ipub-form-item prop="countryCode" label="Country / Territory">
            <ipub-select
              placeholder="Select Country / Territory"
              search
              :clear="true"
              @search="countrySearch"
              v-model="params.countryCode"
            >
              <ipub-select-option
                v-for="item in countryList"
                :value="item.code"
                :key="'countryList' + item.code"
              >
                {{ item.name }}
                <template v-if="item.otherName"
                  >({{ item.otherName }})</template
                >
              </ipub-select-option>
            </ipub-select>
          </ipub-form-item>
        </ipub-col>
        <ipub-col :span="3">
          <ipub-form-item prop="assignedEditor" label="Assigned Editor">
            <ipub-input
              placeholder="Input Assigned Editor"
              v-model="params.assignedEditor"
            />
          </ipub-form-item>
        </ipub-col>
        <ipub-col :span="3">
          <ipub-form-item prop="academicEditor" label="Academic Editor">
            <ipub-input
              placeholder="Input Academic Editor"
              v-model="params.academicEditor"
            />
          </ipub-form-item>
        </ipub-col>
        <ipub-col :span="3">
          <ipub-form-item prop="totalApc" label="Total APC">
            <ipub-input
              number
              :fixed="2"
              placeholder="Input Total APC"
              v-model="params.totalApc"
            />
          </ipub-form-item>
        </ipub-col>
      </ipub-row>
      <ipub-form-item prop="title" label="Title">
        <ipub-editor :height="150" @blur="handleBlur" v-model="params.title" />
      </ipub-form-item>
      <ipub-form-item label="Other Title" prop="otherTitle">
        <ipub-editor
          v-model="params.otherTitle"
          :height="150"
          @blur="handleBlur"
        />
      </ipub-form-item>
      <div class="mar-b-30">
        <a
          @click="
            () => {
              showAuthorList = !showAuthorList
            }
          "
        >
          <div
            class="ipub-p2 fw-bd flex cusor-point mar-b-10"
            style="border-bottom: 1px solid #b4c1c6"
          >
            Authors
            <div class="ipub-p2">
              <ipub-icon
                :class="['mar-l-10 rotate', showAuthorList ? 'rotate-180' : '']"
                type="iconArrow"
              />
            </div>
          </div>
        </a>
        <div v-show="showAuthorList">
          <div class="flex mar-b-10">
            <ipub-input
              v-model.trim="authorEmail"
              style="width: 300px; margin-right: 20px"
              placeholder="Enter Email"
            />
            <ipub-button
              type="primary"
              class="ipub-h3 line-h-40"
              @click="addAuthor"
              icon="iconAdd"
            ></ipub-button>
          </div>
          <ipub-table
            :columns="authorColumns"
            :pagination="false"
            :showEmpty="false"
            draggable
            @drag="
              (e) => {
                setSort(e, 'authorList')
              }
            "
            :dataSource="params.authorList"
            style="padding-bottom: 0"
          >
            <template v-slot:name="{ record }">
              <!-- <a class="link-color"
                 v-if="record.userId"
                 @click="showAuthorDetail(record)">{{record.firstName}} {{record.lastName}}</a> -->
              <font class="font-color"
                >{{ record.firstName }} {{ record.lastName }}</font
              >
              <ipub-label
                v-if="record.ebed"
                size="small"
                style="margin-left: 4px; padding: 0 5px"
                type="link"
              >
                EB
              </ipub-label>
              <ipub-label
                v-if="record.rbed"
                size="small"
                style="margin-left: 4px; padding: 0 5px"
                type="link"
              >
                RB
              </ipub-label>
              <ipub-label
                v-if="record.geed"
                size="small"
                style="margin-left: 4px; padding: 0 5px"
                type="link"
              >
                GE
              </ipub-label>
            </template>
            <template v-slot:actions="{ record, index }">
              <ipub-tooltip class="border-right">
                <div slot="title">Edit</div>
                <ipub-icon
                  type="iconEdit"
                  @click="edit(record, index, 'authorList')"
                />
              </ipub-tooltip>
              <ipub-tooltip>
                <div slot="title">Delete</div>
                <ipub-icon
                  type="iconDelete"
                  @click="del(index, 'authorList')"
                />
              </ipub-tooltip>
            </template>
          </ipub-table>

          <!--          新增作者团体-->
          <div class="flex mar-b-10">
            <ipub-input
              v-model.trim="collaborationName"
              style="width: 300px; margin-right: 20px"
              placeholder="Enter Collaboration"
            />
            <ipub-button
              type="primary"
              class="ipub-h3 line-h-40"
              @click="addCollaborationAuthor"
              icon="iconAdd"
            ></ipub-button>
          </div>

          <ipub-table
            :columns="authorColumns"
            :pagination="false"
            :showEmpty="false"
            draggable
            @drag="
              (e) => {
                setSort(e, 'collaborationAuthorList')
              }
            "
            :dataSource="collaborationAuthorList"
          >
            <template v-slot:name="{ record }">
              <!-- <a class="link-color"
                 v-if="record.userId"
                 @click="showAuthorDetail(record)">{{record.firstName}} {{record.lastName}}</a> -->
              <font class="font-color"
                >{{ record.firstName }} {{ record.lastName }}</font
              >
              <ipub-label
                v-if="record.ebed"
                size="small"
                style="margin-left: 4px; padding: 0 5px"
                type="link"
              >
                EB
              </ipub-label>
              <ipub-label
                v-if="record.rbed"
                size="small"
                style="margin-left: 4px; padding: 0 5px"
                type="link"
              >
                RB
              </ipub-label>
              <ipub-label
                v-if="record.geed"
                size="small"
                style="margin-left: 4px; padding: 0 5px"
                type="link"
              >
                GE
              </ipub-label>
            </template>
            <template v-slot:actions="{ record, index }">
              <ipub-tooltip class="border-right">
                <div slot="title">Edit</div>
                <ipub-icon
                  type="iconEdit"
                  @click="
                    edutCollaborationAuthorList(
                      record,
                      index,
                      'collaborationAuthorList'
                    )
                  "
                />
              </ipub-tooltip>
              <ipub-tooltip>
                <div slot="title">Delete</div>
                <ipub-icon
                  type="iconDelete"
                  @click="delCollaborationAuthorList(index)"
                />
              </ipub-tooltip>
            </template>
          </ipub-table>
        </div>
      </div>
      <div class="mar-b-30">
        <div
          class="ipub-p2 fw-bd flex mar-b-10"
          style="border-bottom: 1px solid #b4c1c6"
        >
          <a
            class="flex"
            @click="
              () => {
                showOrgList = !showOrgList
              }
            "
          >
            Affiliations
            <div class="ipub-p2">
              <ipub-icon
                :class="['mar-l-10 rotate', showOrgList ? 'rotate-180' : '']"
                type="iconArrow"
              />
            </div>
          </a>
          <a
            @click="add('affiliationList')"
            class="ipub-p2 mar-l-20 link-color a-manuscripts-link"
          >
            <ipub-icon type="iconAdd" />
            <b> Add New Affilications</b>
          </a>
        </div>
        <div v-show="showOrgList">
          <ipub-table
            :columns="orgColumns"
            :pagination="false"
            :showEmpty="false"
            draggable
            @drag="
              (e) => {
                setSort(e, 'affiliationList')
              }
            "
            :dataSource="params.affiliationList"
          >
            <template v-slot:actions="{ record, index }">
              <ipub-tooltip class="border-right">
                <div slot="title">Edit</div>
                <ipub-icon
                  type="iconEdit"
                  @click="edit(record, index, 'affiliationList')"
                />
              </ipub-tooltip>
              <ipub-tooltip>
                <div slot="title">Delete</div>
                <ipub-icon
                  type="iconDelete"
                  @click="del(index, 'affiliationList')"
                />
              </ipub-tooltip>
            </template>
          </ipub-table>
        </div>
        <div v-show="showOrgList">
          <ipub-empty
            v-if="params.affiliationList.length == 0"
            type="row"
            icon="iconEditPen"
          >
            <div class="default-color ipub-h1 tc">No Affiliations</div>
            <div
              style="margin-top: -4px"
              class="mar-b-40 default-color ipub-p1 tc"
            >
              There is no Affiliations yet.
              <a @click="add('affiliationList')" class="link-color"
                >Click here</a
              >
              to add new one
            </div>
          </ipub-empty>
        </div>
      </div>
      <ipub-form-item label="Author Notes">
        <ipub-editor :height="200" v-model="params.detail.authorComment" />
      </ipub-form-item>
      <ipub-form-item label="highlights">
        <div slot="label" class="mar-b-10 border-bottom">
          <span class="ipub-p2 fw-bd"
            >Highlight
            <ipub-icon
              :class="[
                'mar-l-10 rotate ipub-c-p',
                showHighlightsed ? 'rotate-180' : '',
              ]"
              @click="
                () => {
                  showHighlightsed = !showHighlightsed
                }
              "
              type="iconArrow"
            />
          </span>
          <ipub-radio
            class="mar-l-40"
            :checked="params.detail.highlightsed"
            @change="
              () => {
                params.detail.highlightsed = !params.detail.highlightsed
              }
            "
            label="Show on Website"
          />
        </div>
        <ipub-editor
          :height="200"
          v-if="showHighlightsed"
          v-model="params.detail.highlights"
        />
      </ipub-form-item>
      <ipub-form-item label="highlights">
        <div slot="label" class="ipub-p2 mar-b-10 border-bottom">
          <span class="fw-bd">Graphical Abstract</span>
          <ipub-icon
            :class="[
              'mar-l-10 rotate ipub-c-p',
              showGraphical ? 'rotate-180' : '',
            ]"
            @click="
              () => {
                showGraphical = !showGraphical
              }
            "
            type="iconArrow"
          />
          <ipub-tooltip placement="right" class="mar-l-10">
            <div slot="title">
              <div class="font-color ipub-p4">
                Suggested size: 800 * 500px; Support .png / .jpg; File size
                should be less than 500K.
              </div>
            </div>
            <ipub-icon type="iconInfo" />
          </ipub-tooltip>
          <ipub-radio
            class="mar-l-40"
            :checked="params.detail.graphicaled"
            @change="
              () => {
                params.detail.graphicaled = !params.detail.graphicaled
              }
            "
            label="Show in abstract page"
          />
        </div>
        <div v-show="showGraphical">
          <!-- <ipub-upload-cropper @change="(e)=>{handleUpload('graphical', e)}"
                               :canWidth="520"
                               :canHeight="240"
                               :scales="1"
                               :size="500"
                               clearable
                               :imgUrl="`${params.detail.graphical?domain+'/'+params.detail.graphical:''}`" /> -->
          <!-- <ipub-form-item prop="picture"
                          label="Image Upload">

            <div class="font-color ipub-p4"> - Suggestion Size: 460 x 140 px; Only support .png and .jpg; File size need to be less than 2MB.</div>
          </ipub-form-item> -->
          <ipub-upload
            class="mar-b-20 ipub-c-p"
            @change="
              (e) => {
                handleUpload('graphical', e)
              }
            "
          >
            <div class="upload-content">
              <img
                v-if="params.detail.graphical"
                :src="`${
                  params.detail.graphical
                    ? domain + '/' + params.detail.graphical
                    : ''
                }`"
                alt=""
              />
              <ipub-icon v-else type="iconAdd" />
            </div>
          </ipub-upload>
        </div>
      </ipub-form-item>
      <ipub-form-item
        label="Figure Legend"
        v-if="showGraphical"
        prop="figureLegend"
      >
        <ipub-editor :height="400" v-model="params.detail.figureLegend" />
      </ipub-form-item>
      <div class="border-bottom ipub-p2 mar-b-10 border-bottom">
        <span class="fw-bd">Declaration</span>
        <ipub-icon
          :class="[
            'mar-l-10 rotate ipub-c-p',
            showDeclaration ? 'rotate-180' : '',
          ]"
          @click="
            () => {
              showDeclaration = !showDeclaration
            }
          "
          type="iconArrow"
        />
        <ipub-radio
          class="mar-l-40"
          :checked="params.detail.declarationed"
          @change="
            () => {
              params.detail.declarationed = !params.detail.declarationed
            }
          "
          label="Show in both abstract & full text page"
        />
      </div>
      <div v-if="showDeclaration">
        <ipub-form-item label="Text" prop="declarationText">
          <ipub-textarea
            :height="400"
            v-model="params.detail.declarationText"
          />
        </ipub-form-item>
        <ipub-form-item label="Related Article" prop="relatedArticleId">
          <site-search-group
            v-model.trim="keyword"
            placeholder="DOI / Manuscript No."
            @search="bind"
            @clear="handleClear"
          />
          <a @click="linkto" v-if="articalQuery && articalQuery.id"
            >Article Title:
            <font class="link-color">{{ articalQuery.titleText }}</font></a
          >
        </ipub-form-item>
        <ipub-form-item label="Link Text" prop="relatedLinkText">
          <ipub-textarea v-model="params.detail.relatedLinkText" />
        </ipub-form-item>
      </div>

      <ipub-form-item
        :label="isRe ? 'Retraction Announcement' : 'Abstract'"
        prop="summary"
      >
        <ipub-editor :height="400" v-model="params.summary" />
      </ipub-form-item>

      <ipub-form-item
        :label="isRe ? 'Other Retraction Announcement' : 'Other Abstract'"
        prop="otherSummary"
      >
        <ipub-editor v-model="params.otherSummary" :height="400" />
      </ipub-form-item>

      <ipub-form-item label="Keywords" prop="keywords">
        <ipub-input
          placeholder="Keywords can be separated by a semicolon or carriage return"
          tag
          :select="keywordList"
          @tagInput="getKeyword"
          v-model.trim="params.keywords"
        />
      </ipub-form-item>
      <ipub-form-item label="Other Keywords" prop="otherKeywords">
        <ipub-input
          v-model.trim="params.otherKeywords"
          :select="otherKeywordList"
          placeholder="Keywords can be separated by a semicolon or carriage return"
          tag
          @tagInput="getKeyword"
        />
      </ipub-form-item>
      <div class="mar-b-30">
        <div
          class="ipub-p2 fw-bd cusor-point relative flex mar-b-10"
          style="border-bottom: 1px solid #b4c1c6"
        >
          <a
            @click="
              () => {
                showRefList = !showRefList
              }
            "
            class="flex"
          >
            References
            <div class="ipub-p2">
              <ipub-icon
                :class="['mar-l-10 rotate', showRefList ? '' : 'rotate-180']"
                type="iconArrow"
              />
            </div>
          </a>
          <a
            @click="add('areferenceList')"
            class="ipub-p2 mar-l-20 link-color a-manuscripts-link"
          >
            <ipub-icon type="iconAdd" />
            <strong> Add New Reference</strong>
          </a>
          <div class="right-option">
            <ipub-radio
              label="Show on abstract page"
              @change="changeDisplayed"
              :checked="params.displayed"
            />
          </div>
        </div>
        <div v-show="!showRefList">
          <ipub-table
            :columns="RefColumns"
            :pagination="false"
            :showEmpty="false"
            draggable
            @drag="
              (e) => {
                setSort(e, 'areferenceList')
              }
            "
            :dataSource="params.areferenceList"
          >
            <template v-slot:actions="{ record, index }">
              <ipub-tooltip class="border-right">
                <div slot="title">Edit</div>
                <ipub-icon
                  type="iconEdit"
                  @click="edit(record, index, 'areferenceList')"
                />
              </ipub-tooltip>
              <ipub-tooltip>
                <div slot="title">Delete</div>
                <ipub-icon
                  type="iconDelete"
                  @click="del(index, 'areferenceList')"
                />
              </ipub-tooltip>
            </template>
          </ipub-table>
        </div>
        <div v-show="showRefList">
          <ipub-empty
            v-if="params.areferenceList.length == 0"
            type="row"
            icon="iconEditPen"
          >
            <div class="default-color ipub-h1 tc">No Reference</div>
            <div
              style="margin-top: -4px"
              class="mar-b-40 default-color ipub-p1 tc"
            >
              There is no Reference yet.
              <a @click="add('areferenceList')" class="link-color"
                >Click here</a
              >
              to add new one
            </div>
          </ipub-empty>
        </div>
      </div>
      <div class="mar-b-30">
        <div
          class="ipub-p2 fw-bd flex mar-b-10"
          style="border-bottom: 1px solid #b4c1c6"
        >
          <a
            @click="
              () => {
                showFounList = !showFounList
              }
            "
            class="flex"
          >
            Funding
            <div class="ipub-p2">
              <ipub-icon
                :class="['mar-l-10 rotate', showFounList ? 'rotate-180' : '']"
                type="iconArrow"
              />
            </div>
          </a>
          <a
            @click="add('afoundationList')"
            class="ipub-p2 mar-l-20 link-color a-manuscripts-link"
          >
            <ipub-icon type="iconAdd" />
            <strong> Add Funding</strong>
          </a>
        </div>

        <div v-show="showFounList">
          <ipub-table
            :columns="FounColumns"
            :pagination="false"
            :showEmpty="false"
            draggable
            @drag="
              (e) => {
                setSort(e, 'afoundationList')
              }
            "
            :dataSource="params.afoundationList"
          >
            <template v-slot:actions="{ record, index }">
              <ipub-tooltip class="border-right">
                <div slot="title">Edit</div>
                <ipub-icon
                  type="iconEdit"
                  @click="edit(record, index, 'afoundationList')"
                />
              </ipub-tooltip>
              <ipub-tooltip>
                <div slot="title">Delete</div>
                <ipub-icon
                  type="iconDelete"
                  @click="del(index, 'afoundationList')"
                />
              </ipub-tooltip>
            </template>
          </ipub-table>
        </div>
        <div v-show="showFounList">
          <ipub-empty
            v-if="!params.afoundationList"
            type="row"
            icon="iconEditPen"
          >
            <div class="default-color ipub-h1 tc">No Funding</div>
            <div
              style="margin-top: -4px"
              class="mar-b-40 default-color ipub-p1 tc"
            >
              There is no Funding yet.
              <a @click="add('afoundationList')" class="link-color"
                >Click here</a
              >
              to add new one
            </div>
          </ipub-empty>
        </div>
      </div>

      <ipub-form-item prop="copyright" label="Copyright">
        <ipub-input :height="150" v-model="params.copyrightHolder" />
      </ipub-form-item>
      <ipub-form-item prop="license" label="License">
        <ipub-editor :height="150" v-model="params.license" />
      </ipub-form-item>

      <ipub-form-item label="Conflict of Interest">
        <ipub-textarea
          placeholder="Conflict of Interest"
          v-model.trim="params.detail.conflict"
        />
      </ipub-form-item>
      <ipub-row :gutter="20">
        <ipub-col :span="4">
          <ipub-form-item prop="xmlFile">
            <div class="ipub-p2 fw-bd mar-b-10 relative" slot="label">
              XML File
            </div>
            <ipub-upload-input
              :file="params.xmlFile"
              class="mar-b-20"
              :domain="domain"
              @delete="delFile('xmlFile')"
              @change="
                (e) => {
                  handleUpload('xmlFile', e)
                }
              "
            />
          </ipub-form-item>
        </ipub-col>
        <ipub-col :span="4">
          <ipub-form-item prop="annexFile">
            <div class="ipub-p2 fw-bd mar-b-10 relative" slot="label">
              XML Attachment
            </div>
            <ipub-upload-input
              :file="params.annexFile"
              class="mar-b-20"
              :directory="true"
              :domain="domain"
              @delete="delFile('annexFile')"
              @change="
                (e) => {
                  handleUpload('annexFile', e)
                }
              "
            />
          </ipub-form-item>
        </ipub-col>
        <ipub-col :span="4">
          <ipub-form-item prop="pdfFile">
            <div class="ipub-p2 fw-bd mar-b-10 relative" slot="label">
              PDF File
            </div>
            <ipub-upload-input
              :file="params.pdfFile"
              class="mar-b-20"
              :domain="domain"
              @delete="delFile('pdfFile')"
              @change="
                (e) => {
                  handleUpload('pdfFile', e)
                }
              "
            />
          </ipub-form-item>
        </ipub-col>
      </ipub-row>
      <ipub-form-item prop="otherPdfFile">
        <div slot="label" class="ipub-p2 fw-bd mar-b-10 relative">
          Other PDF File
        </div>
        <ipub-upload-input
          :domain="domain"
          :file="params.otherPdfFile"
          class="mar-b-20"
          @change="
            (e) => {
              handleUpload('otherPdfFile', e)
            }
          "
          @delete="delFile('otherPdfFile')"
        />
      </ipub-form-item>
      <ipub-form-item>
        <ipub-upload-label
          style="min-width: 300px"
          label="Other Attachment(s)"
          multiple
          :domain="domain"
          :loading="loading"
          @change="
            (e) => {
              handleUpload('supplyFileList', e)
            }
          "
          :fileList="params.supplyFileList"
        />
      </ipub-form-item>
    </ipub-form>
    <div class="btns">
      <ipub-button
        v-if="!params.id"
        plain
        style="width: 150px"
        @click="
          () => {
            $router.back(-1)
          }
        "
        type="primary"
        >Go Back
      </ipub-button>
      <!-- <ipub-button plain
                   style="width:150px"
                   @click="preview"
                   type="primary">{{LANGUAGE[language].preview}}</ipub-button> -->
      <ipub-button plain style="width: 150px" @click="submit('')" type="primary"
        >Save
      </ipub-button>
      <ipub-button
        v-if="!params.id"
        style="width: 150px"
        @click="submit('PUBLISHED')"
        type="primary"
        >Save & Publish
      </ipub-button>
    </div>
    <Add
      :isVisable="authorListDialog"
      :inputData="inputData"
      @confirm="
        (e) => {
          confirm(e, 'authorList')
        }
      "
      @cancel="cancel('authorList')"
    />
    <Detail
      :isVisable="authorDetailDialog"
      :inputData="inputData"
      @cancel="cancel('authorDetail')"
    />
    <AddOrg
      :isVisable="affiliationListDialog"
      :inputData="inputData"
      @confirm="
        (e) => {
          confirm(e, 'affiliationList')
        }
      "
      @cancel="cancel('affiliationList')"
    />
    <AddRef
      :isVisable="areferenceListDialog"
      :inputData="inputData"
      @confirm="
        (e) => {
          confirm(e, 'areferenceList')
        }
      "
      @cancel="cancel('areferenceList')"
    />
    <AddFoun
      :isVisable="afoundationListDialog"
      :inputData="inputData"
      @confirm="
        (e) => {
          confirm(e, 'afoundationList')
        }
      "
      @cancel="cancel('afoundationList')"
    />
    <tipInfo-dialog
      :isVisable="isVisable"
      :inputData="inputData"
      @confirm="publishAfter"
      @cancel="cancel"
    ></tipInfo-dialog>
    <Add
      :isVisable="collaborationAuthorListDialog"
      :inputData="inputCollaborationAuthor"
      @confirm="
        (e) => {
          confirm(e, 'collaborationAuthorList')
        }
      "
      @cancel="cancel('collaborationAuthorList')"
    />
  </div>
</template>

<style lang="scss" scoped>
@import './main.scss';
</style>

<script>
import main from './main'

export default main
</script>
